import React, { useState } from 'react';
import { Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { addTransactionFlow, updateTransactionFlow, deleteTransactionFlow } from '../../api/transactionsAPI';
import StatusBadge from '../../../../components/status/StatusBadge';
import { useNotification } from '../../../../context/NotificationContext';
import { useModal } from '../../../../context/ModalContext';
import Tooltip from '../../../../components/ui/Tooltip';
import CustomSelect from '../../../../components/ui/CustomSelect';
import { formatCurrency, formatDate } from '../../../../utils/formatters';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/Card';

const FLOW_TYPES = {
  CALL_FOR_FUNDS: "Appel de fonds",
  CAPITAL_CALL: "Appel de fonds",
  SUBSCRIPTION: "Souscription",
  DISTRIBUTION: "Distribution (Rendement)",
  REPAYMENT: "Remboursement (Capital)",
  REDEMPTION: "Rachat (Capital)",
  INTEREST: "Intérêts",
  DIVIDEND: "Dividendes",
  FEES: "Frais de gestion",
  MANAGEMENT_FEE: "Frais de gestion"
};

export default function TransactionFlows({ transaction, onRefresh }) {
  const { showToast } = useNotification();
  const { confirm } = useModal();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'CALL_FOR_FUNDS',
    amount: '',
    dueDate: '',
    participantId: ''
  });

  const flows = transaction.flows || [];

  const handleAddFlow = async (e) => {
    e.preventDefault();
    try {
      await addTransactionFlow(transaction.id, {
        ...formData,
        amount: Number(formData.amount)
      });
      showToast("Flux financier ajouté avec succès", "success");
      setShowForm(false);
      setFormData({ type: 'CALL_FOR_FUNDS', amount: '', dueDate: '', participantId: '' });
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast("Erreur lors de l'ajout du flux", "error");
    }
  };

  const handleCompleteFlow = async (flowId) => {
    try {
      await updateTransactionFlow(transaction.id, flowId, { status: 'COMPLETED' });
      showToast("Flux marqué comme traité", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast("Erreur lors de la mise à jour du flux", "error");
    }
  };

  const handleDeleteFlow = async (flowId) => {
    const isConfirmed = await confirm({
      title: "Supprimer le flux",
      message: "Voulez-vous vraiment supprimer ce flux ?",
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });
    if (!isConfirmed) return;
    try {
      await deleteTransactionFlow(transaction.id, flowId);
      showToast("Flux supprimé avec succès", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      showToast("Erreur lors de la suppression du flux", "error");
    }
  };

  const totalAppele = flows
    .filter(f => (f.flowType === 'CAPITAL_CALL' || f.flowType === 'SUBSCRIPTION' || f.type === 'CALL_FOR_FUNDS') && (f.status === 'COMPLETED' || f.status === 'SETTLED'))
    .reduce((acc, curr) => acc + Number(curr.grossAmount || curr.amount || 0), 0);

  const totalDistribue = flows
    .filter(f => (f.flowType === 'DISTRIBUTION' || f.flowType === 'DIVIDEND' || f.flowType === 'INTEREST' || f.type === 'DISTRIBUTION') && (f.status === 'COMPLETED' || f.status === 'SETTLED'))
    .reduce((acc, curr) => acc + Number(curr.grossAmount || curr.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Synthèse */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="shadow-hp-sm border-l-4 border-l-[var(--hp-info-500)]">
          <CardContent className="p-5">
            <h4 className="text-sm font-medium text-subtle">Total Appelé (Encaissé)</h4>
            <p className="mt-1 text-2xl font-bold text-primary">
              {formatCurrency(totalAppele, transaction.currency)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="shadow-hp-sm border-l-4 border-l-[var(--hp-success-500)]">
          <CardContent className="p-5">
            <h4 className="text-sm font-medium text-subtle">Total Distribué</h4>
            <p className="mt-1 text-2xl font-bold text-primary">
              {formatCurrency(totalDistribue, transaction.currency)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Liste des flux */}
      <Card className="shadow-hp-sm overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b border-[var(--hp-color-border-muted)] bg-surface px-6 py-4">
          <CardTitle className="text-lg font-medium text-primary">
            Échéancier des flux financiers
          </CardTitle>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center text-sm font-medium text-[var(--hp-info-600)] transition-colors hover:text-[var(--hp-info-700)]"
          >
            <Plus className="mr-1 h-4 w-4" />
            Nouveau flux
          </button>
        </CardHeader>

        {showForm && (
          <div className="border-b border-[var(--hp-color-border-muted)] bg-surface-muted/50 p-6">
            <form onSubmit={handleAddFlow} className="grid grid-cols-1 items-end gap-4 md:grid-cols-5">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-subtle">Type de flux</label>
                <CustomSelect
                  value={formData.type}
                  onChange={(val) => setFormData({ ...formData, type: val })}
                  options={Object.entries(FLOW_TYPES).map(([k, v]) => ({ value: k, label: v }))}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-subtle">Montant</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="block w-full rounded-md border border-[var(--hp-color-border-muted)] bg-surface px-3 py-2 text-sm shadow-sm focus:border-[var(--hp-info-500)] focus:outline-none focus:ring-1 focus:ring-[var(--hp-info-500)]"
                  placeholder="Ex: 50000"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-subtle">Date d'échéance</label>
                <input
                  type="date"
                  required
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="block w-full rounded-md border border-[var(--hp-color-border-muted)] bg-surface px-3 py-2 text-sm shadow-sm focus:border-[var(--hp-info-500)] focus:outline-none focus:ring-1 focus:ring-[var(--hp-info-500)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-subtle">Cible (Optionnel)</label>
                <CustomSelect
                  value={formData.participantId}
                  onChange={(val) => setFormData({ ...formData, participantId: val })}
                  options={[
                    { value: "", label: "Global (Transaction)" },
                    ...(transaction.participants?.map(p => ({
                      value: p.id,
                      label: p.famille?.nom || p.societe?.denomination || p.id
                    })) || [])
                  ]}
                />
              </div>
              <div>
                <button
                  type="submit"
                  className="w-full rounded-md bg-[var(--hp-info-600)] py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[var(--hp-info-700)] focus:outline-none focus:ring-2 focus:ring-[var(--hp-info-500)] focus:ring-offset-2"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        )}

        {flows.length === 0 ? (
          <div className="p-8 text-center text-subtle">
            Aucun flux financier n'a été programmé.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--hp-color-border-muted)]">
              <thead className="bg-surface-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-subtle">
                    Échéance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-subtle">
                    Type
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-subtle">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-subtle">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-subtle">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--hp-color-border-muted)] bg-surface">
                {flows.map((flow) => (
                  <tr key={flow.id} className="transition-colors hover:bg-surface-muted/30">
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-primary">
                      {formatDate(flow.dueDate)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-primary">
                      {FLOW_TYPES[flow.flowType || flow.type] || flow.flowType || flow.type}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-bold text-primary tabular-nums">
                      {formatCurrency(flow.grossAmount || flow.amount, flow.currency || transaction.currency)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <StatusBadge status={flow.status} type="flow" />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      {(flow.status === 'PENDING' || flow.status === 'PLANNED') && (
                        <Tooltip content="Marquer comme traité">
                          <button
                            onClick={() => handleCompleteFlow(flow.id)}
                            className="mr-3 text-[var(--hp-success-600)] transition-colors hover:text-[var(--hp-success-700)]"
                          >
                            <CheckCircle2 className="inline h-5 w-5" />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip content="Supprimer">
                        <button
                          onClick={() => handleDeleteFlow(flow.id)}
                          className="text-[var(--hp-danger-600)] transition-colors hover:text-[var(--hp-danger-700)]"
                        >
                          <Trash2 className="inline h-5 w-5" />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
